import { useState, useCallback } from 'react';
import { toast } from 'sonner';

interface RetryConfig {
  maxAttempts?: number;
  baseDelay?: number;
  maxDelay?: number;
  backoffFactor?: number;
  retryableErrors?: string[];
}

interface RetryState {
  attempts: number;
  isRetrying: boolean;
  lastError: Error | null;
}

export function useRetry<T extends any[], R>(
  operation: (...args: T) => Promise<R>,
  config: RetryConfig = {}
) {
  const {
    maxAttempts = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    backoffFactor = 2,
    retryableErrors = ['NetworkError', 'TimeoutError', 'fetch']
  } = config;

  const [retryState, setRetryState] = useState<RetryState>({
    attempts: 0,
    isRetrying: false,
    lastError: null
  });

  const isRetryableError = useCallback((error: Error): boolean => {
    const errorMessage = error.message.toLowerCase();
    const errorName = error.name.toLowerCase();
    
    return retryableErrors.some(pattern => 
      errorMessage.includes(pattern.toLowerCase()) || 
      errorName.includes(pattern.toLowerCase())
    ) || 
    // Check for HTTP 5xx errors
    (errorMessage.includes('500') || errorMessage.includes('502') || 
     errorMessage.includes('503') || errorMessage.includes('504'));
  }, [retryableErrors]);

  const calculateDelay = useCallback((attempt: number): number => {
    const delay = baseDelay * Math.pow(backoffFactor, attempt);
    return Math.min(delay, maxDelay);
  }, [baseDelay, backoffFactor, maxDelay]);

  const executeWithRetry = useCallback(async (...args: T): Promise<R> => {
    setRetryState(prev => ({ ...prev, isRetrying: true, attempts: 0 }));

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      try {
        setRetryState(prev => ({ ...prev, attempts: attempt + 1 }));
        const result = await operation(...args);
        
        setRetryState({
          attempts: attempt + 1,
          isRetrying: false,
          lastError: null
        });
        
        return result;
      } catch (error) {
        const isLastAttempt = attempt === maxAttempts - 1;
        const err = error as Error;
        
        setRetryState(prev => ({ 
          ...prev, 
          lastError: err,
          isRetrying: !isLastAttempt 
        }));

        if (isLastAttempt) {
          throw err;
        }

        if (!isRetryableError(err)) {
          throw err;
        }

        const delay = calculateDelay(attempt);
        
        if (attempt < maxAttempts - 1) {
          toast.info(`Retrying in ${Math.round(delay / 1000)}s...`, {
            description: `Attempt ${attempt + 2} of ${maxAttempts}`
          });
          
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    throw new Error('Retry logic failed unexpectedly');
  }, [operation, maxAttempts, isRetryableError, calculateDelay]);

  const retry = useCallback(async (...args: T): Promise<R> => {
    return executeWithRetry(...args);
  }, [executeWithRetry]);

  const reset = useCallback(() => {
    setRetryState({
      attempts: 0,
      isRetrying: false,
      lastError: null
    });
  }, []);

  return {
    retry,
    reset,
    ...retryState
  };
}